import React, { useCallback, useState } from 'react';
import { IconButton, Slide, Button, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import useStyles from './ReportFab.styles';
import Form from 'global/components/form';
import { useSelector, useDispatch } from 'react-redux';
import { get, set, size, uniq } from 'global/lib/lodash';
import moment from 'moment';
import sendData from 'redux/actions/sendData';
import { userHasPermissions, getAccountSetting } from 'redux/actions/userLogin';
import { withRouter } from 'react-router-dom';
import setUserRealm from 'redux/actions/userRealm';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { SETTINGS } from 'feature/admin/account-settings';
import TemplateModal from './template-modal';
import { FullScreen } from 'global/components/modal';
import { ensureArray } from '../../../global/utils';
import { useFilter } from '../../../global/hooks/useFilter';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const getOnlyTiersFromScorecards = (scorecards = []) => {
  const allTiers = ['division', 'community', 'lot'];

  let tiers = [];
  scorecards.map((card) => {
    let cardTiers = get(card, 'config.allowedTiers', allTiers);

    if (!size(cardTiers)) {
      cardTiers = allTiers;
    }

    tiers = [...tiers, ...cardTiers];
  });

  tiers = uniq(tiers);

  return size(tiers) > 0 ? tiers : undefined;
};

const ReportFab = (props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  let [formRealm, setFormRealm] = useState();
  const realm = useSelector((state) => get(state, 'auth.realm'));
  const [formLoading, setFormLoading] = useState(false);
  const [template, setTemplate] = useState();
  const [error, setError] = useState();
  const [fs, setfs] = useState({});
  const dispatch = useDispatch();
  const fhook = useFilter();

  const isTemplate = !!template;

  const toggleTemplate = (dir) => {
    setTemplateModal((s) => ({ ...s, open: dir !== undefined ? dir : !s.open }));
  };

  const startTemplateCreate = () => {
    setTemplate({});
    onDefaultSelect();
  };

  const onSelectTemplate = async (temp) => {
    const tab = get(temp,'accessRealmTier') === 'division' ? 'info' : 'scores';
    return await formSubmit(temp, tab);
  };

  const onDefaultSelect = () => {
    toggleTemplate();
    setOpen(true);
  };

  const [templateModal, setTemplateModal] = useState({
    open: false,
    onClose: () => toggleTemplate(),
    onSelectTemplate,
    onDefaultSelect,
    startTemplateCreate,
  });

  const realmId = get(formRealm, 'id', null);
  const temp_realm = get(realm, 'id');

  const users = useRetrieveData(
    { url: '/users' },
    {
      useEffect: true,
      waitFor: realmId,
      build: ['users'],
      params: { filter: { content_realm_id: realmId } },
      useCache: { reload: 1 },
    }
  );
  const scorecards = useRetrieveData(
    { url: '/scorecards' },
    {
      build: ['scorecards'],
      useCache: { reload: 20 },
      params: {
        include: 'realms,userGroups',
      }
    }
  );
  const templates = useRetrieveData(
    { url: '/assessment-templates' },
    {
      build: ['assessments'],
      useEffect: true,
      params: {
        include: 'authors,editors,realm,scorecards',
        filter: temp_realm && { realm: temp_realm },
      },
    }
  );

  function handleClickOpen() {
    const shouldShowTemplate = size(templates.data) > 0 || userHasPermissions('createReportTemplates');
    if (shouldShowTemplate) {
      toggleTemplate();
    } else {
      setOpen(true);
    }
  }

  function handleClose() {
    setOpen(false);
    toggleTemplate(false);
    setTemplate();
  }

  const formatData = (values) => {
    const relationships = {};
    const scorecards = [];
    const editors = get(values, 'editors');
    const viewers = get(values, 'viewers');
    const assignees = get(values, 'assignees');

    const isFromTemplate = !!get(values, 'template_id');

    const vis = get(values, 'generalVisibility', 'normal');

    for (let k in values) {
      if (k.startsWith('scorecard')) {
        scorecards.push(values[k]);
      }
    }

    const startDateFormat = 'YYYY-MM-DD';
    const createFormat = 'YYYY-MM-DD HH:mm:ss';

    let attributes = {};

    if (isFromTemplate) {
      attributes = {
        realm_id: get(formRealm, 'id', get(values, 'realmId')).toString(),
        name: get(values, 'reportName'),
        template_id: get(values, 'template_id'),
      };
    } else {
      attributes = {
        realm_id: get(formRealm, 'id', get(values, 'realmId')).toString(),
        name: get(values, 'reportName'),
        start_date: moment(get(values, 'startDate', moment())).format(startDateFormat),
        end_date: moment(get(values, 'endDate', moment())).format(startDateFormat),
        completion_date: get(values, 'completionDate') ? moment(get(values, 'completionDate')).format(startDateFormat) : undefined,
        visibility: vis === 'normal' ? 1 : vis === 'global' ? 2 : 0,
        'created-at': moment().format(createFormat),
        'updated-at': moment().format(createFormat),
        status: isTemplate ? 'template' : undefined,
      };

      if (editors) {
        set(relationships, 'editors', {
          data: editors.map((e) => ({ type: 'users', id: get(e, 'id') })),
        });
      }

      if (viewers) {
        set(relationships, 'viewers', {
          data: viewers.map((e) => ({ type: 'users', id: get(e, 'id') })),
        });
      }

      set(relationships, 'scorecards', {
        data: scorecards.map((sc) => ({ type: 'scorecards', id: sc })),
      });
    }

    if (assignees) {
      set(relationships, 'assignees', {
        data: ensureArray(assignees).map((e) => ({ type: 'users', id: get(e, 'id') })),
      });
    }

    return {
      data: {
        type: isTemplate ? 'assessment-templates' : 'assessments',
        attributes,
        relationships,
      },
    };
  };

  const defaultAccess = getAccountSetting(SETTINGS.assessmentAccess);

  const formSubmit = async (values, tab) => {
    try {
      setFormLoading(true);
      const data = formatData(values);
      const resp = await sendData({
        url: isTemplate ? '/assessment-templates' : '/assessments',
        method: 'post',
        data,
      });
      const id = get(resp, 'data.data.id');
      props.history.push(`${isTemplate ? 'assessment-template' : 'assessment'}/${id}${tab ? `/${tab}` : ''}`);
      setFormLoading(false);
      handleClose();
    } catch (err) {
      setFormLoading(false);
      setError(`${err.message}. Please try again.`);
      return err;
    }
  };

  const formscorecards = fhook.filterScorecards(scorecards.data || [], { filterByUserGroups: true }).map((sc) => ({ ...sc, value: get(sc, 'id'), title: get(sc, 'name') }));
  const onlyTiers = (() => {
    if (isTemplate) {
      return ['builder', 'division', 'community'];
    }

    const cards = Object.keys(fs)
      .map((k) => {
        if (k.startsWith('scorecard')) {
          const id = fs[k];
          const card = formscorecards.find((f) => f.value == id);

          return card;
        }
      })
      .filter((f) => !!f);

    return getOnlyTiersFromScorecards(cards);
  })();

  return (
    <>
      <Tooltip title="Add Assessment" aria-label="Add" onClick={handleClickOpen}>
        <Button disabled={!userHasPermissions('createReports')} color="secondary" variant="contained" className={classes.fab}>
          <AddIcon className={classes.icon} />
          Create New
        </Button>
      </Tooltip>
      <TemplateModal {...templateModal} templates={templates} users={users} />
      <FullScreen open={open} onClose={handleClose} TransitionComponent={Transition} title={isTemplate ? 'New Assessment Template' : 'Create Assessment'} centerContentStyle={{ padding: 0 }}>
        <>
          {isTemplate && (
            <div className={classes.templateBar}>
              You are creating an assessment <b>template</b>, which can be copied as a baseline for future assessments
            </div>
          )}
          <div className={classes.content}>
            {error && (
              <Alert
                severity="error"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setError();
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {error}
              </Alert>
            )}
            <Form
              showPosition={true}
              useStaticScreens={true}
              loading={formLoading}
              onSubmit={formSubmit}
              updateStaticScreens={[onlyTiers, formRealm]}
              loadingMessage={'Please Wait...'}
              onFormState={(formState) => {
                setfs(formState);
                const newRealm = get(formState, 'realm');

                if (!newRealm) {
                  return;
                }

                const realmKey = Object.keys(newRealm)[0];
                const selRealm = get(newRealm, realmKey);
                const rid = get(formRealm, 'id');
                if (selRealm && selRealm.id !== rid) {
                  dispatch(setUserRealm(selRealm));
                  formRealm = selRealm;
                  setFormRealm(selRealm);
                }
              }}
              screens={[
                {
                  title: 'Step 1',
                  description: 'Choose scoring criteria',
                  positionTitle: 'Criteria',
                  fullScreenView: true,
                  inputs: [
                    {
                      id: 'scorecard',
                      type: 'Select',
                      add: (fs, input) => {
                        return get(fs, input.id);
                      },
                      remove: (fs, input) => {
                        return input.id !== 'scorecard';
                      },
                      required: true,
                      data: formscorecards,
                      item: (item, i) => {
                        return { value: item.id, title: item.name };
                      },
                      defaultValue: () =>
                        getAccountSetting(SETTINGS.assessmentScorecard) && formscorecards.find((s) => s.id == getAccountSetting(SETTINGS.assessmentScorecard))
                          ? getAccountSetting(SETTINGS.assessmentScorecard).toString()
                          : undefined,
                      props: {
                        label: 'Choose a Scorecard',
                      },
                    },
                  ],
                },
                {
                  title: 'Step 2',
                  description: 'Choose a location',
                  positionTitle: 'Location',
                  inputs: [
                    {
                      id: 'realm',
                      type: 'RealmDialog',
                      required: true,
                      style: { height: '100%' },
                      defaultValue: () => (realm && realm.tier !== 'builder' && onlyTiers && onlyTiers.indexOf(realm.tier) !== -1 ? { [realmId]: realm } : {}),
                      props: {
                        onlyTiers,
                        addNew: true,
                        canAdd: true,
                        singleSelect: true,
                        continueOnSelect: false,
                        showSelectedText: false,
                        currentRealm: realm || undefined,
                        openToSelected: true,
                        selectTitle: 'Choose a location for the Assessment',
                      },
                    },
                  ],
                },
                {
                  title: 'Step 3',
                  description: `Set the general information`,
                  positionTitle: 'Info',
                  inputs: [
                    {
                      id: 'reportName',
                      type: 'TextField',
                      defaultValue: (formState) => {
                        const newRealm = get(formState, 'realm');
                        const realmKey = Object.keys(newRealm)[0];
                        const selRealm = get(newRealm, realmKey);
                        return get(selRealm, 'name');
                      },
                      props: {
                        label: 'Title',
                      },
                    },
                    [
                      {
                        id: 'startDate',
                        type: 'KeyboardDatePicker',
                        defaultValue: () => moment(),
                        props: {
                          label: 'Visit Start',
                        },
                      },
                      {
                        id: 'endDate',
                        type: 'KeyboardDatePicker',
                        defaultValue: () => moment(),
                        props: {
                          label: 'Visit End',
                        },
                      },
                    ],
                    {
                      id: 'completionDate',
                      type: 'KeyboardDatePicker',
                      defaultValue: () => null,
                      props: {
                        label: 'Delivery Date',
                      },
                    },
                  ],
                },
                {
                  title: 'Step 4',
                  description: `Give or restrict access to this assessment`,
                  useSource: true,
                  positionTitle: 'Access',
                  inputs: [
                    {
                      id: 'assignees',
                      type: 'UserSelect',
                      data: () => users.data,
                      defaultValue: () => null,
                      props: {
                        label: 'Assignee',
                        helperText: isTemplate
                          ? 'This user will be the default Assignee for all assessments created from this template'
                          : 'This user won’t see scores or highlights until the status is set to “Review”',
                        multiple: false,
                        required: get(formRealm, 'tier', '') === 'lot',
                      },
                    },
                    {
                      id: 'editors',
                      type: 'UserSelect',
                      data: () => users.data,
                      defaultValue: () => [],
                      props: {
                        label: 'Editors',
                        helperText: isTemplate
                          ? 'These users can use and edit this template and will be assigned as Editors on all assessments created from this template'
                          : 'These users can edit this assessment',
                      },
                    },
                    {
                      id: 'viewers',
                      type: 'UserSelect',
                      data: () => users.data,
                      props: {
                        label: 'Viewers',
                        helperText: isTemplate
                          ? 'These users will be assigned as Viewers on all assessments created from this template'
                          : `These users can view this assessment but won't see scores or highlights until the status is set to "Review"`,
                      },
                    },
                    {
                      id: 'generalVisibility',
                      type: 'ToggleButton',
                      options: [
                        { title: 'NORMAL', value: 'normal' },
                        { title: 'GLOBAL', value: 'global' },
                        { title: 'HIDDEN', value: 'hidden' },
                      ],
                      defaultValue: () => getAccountSetting(SETTINGS.assessmentAccess),
                      props: {
                        exclusive: true,
                        label: 'General Visibility',
                        helperText: (formState) => {
                          const gVis = get(formState, 'generalVisibility');
                          if (isTemplate) {
                            const keys = {
                              normal: `(default) Assessments created from this template will be assigned normal access: all users with access to that new assessment's location will see it.`,
                              global: `Assessments created from this template will be assigned global access: all users in the company will see it.`,
                              hidden: `Assessments created from this template will be assigned hidden access: no users will see it except the Author and those assigned as the Assignee, Editors, or Viewers.`,
                            };

                            return get(keys, gVis);
                          }
                          const newRealm = get(formState, 'realm');
                          const realmKey = Object.keys(newRealm)[0];
                          const formRealm = get(newRealm, realmKey);
                          const statusText = ` However, they won't see scores or highlights until the status is set to "Final".`;

                          let str;

                          const getText = (key, text) => {
                            let t = '';
                            if (key === defaultAccess) {
                              t += '(default) ';
                            }
                            t += text;
                            return t;
                          };

                          if (gVis === 'normal') {
                            str = `All users with access to ${get(formRealm, 'name', '[Realm Name]')} can view this assessment.${statusText}`;
                          } else if (gVis === 'global') {
                            str = `All users in the company can view this assessment.${statusText}`;
                          } else if (gVis === 'hidden') {
                            str = 'No users can see this assessment except those assigned above.';
                          } else {
                            str = '';
                          }

                          return getText(gVis, str);
                        },
                      },
                    },
                  ],
                },
              ]}
            />
          </div>
        </>
      </FullScreen>
    </>
  );
};

export default withRouter(ReportFab);
